/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

import React, { createContext, ReactElement, ReactNode, useContext, useRef } from "react"
import HeartReportUiStore from "./stores/heart-report-ui-store"
import HeartReportSessionStore from "./stores/heart-report-session-store"

type HeartReportStores = {
  uiStore: HeartReportUiStore
  sessionStore: HeartReportSessionStore
}

const HeartReportContext = createContext<HeartReportStores | null>(null)

const HeartReportContextProvider = ({
  token,
  children,
}: {
  token: string | null
  children: ReactNode
}): ReactElement => {
  const storesRef = useRef<HeartReportStores | null>(null)
  if (!storesRef.current) {
    const uiStore = new HeartReportUiStore()
    storesRef.current = { uiStore, sessionStore: new HeartReportSessionStore(uiStore, token) }
  }
  return <HeartReportContext.Provider value={storesRef.current}>{children}</HeartReportContext.Provider>
}

const useHeartReportStores = (): HeartReportStores => {
  const stores = useContext(HeartReportContext)
  if (!stores) {
    throw new Error("useHeartReportStores must be used within a HeartReportContextProvider")
  }
  return stores
}

export { HeartReportContextProvider, useHeartReportStores }
